import { useMemo } from "react";

import { Line } from "react-chartjs-2";

import { useUser } from "@components/user-context";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { useLandingPageAnalyticsContext } from "@features/landing-page-analytics/hooks";
import { TitlesPlugin } from "@features/metrics-explorer/components/metrics/chart-plugins";
import { type GetLandingPageReportChart_me_landingPageReportChart_rows } from "@north-beam/nb-client-side";

import { ChartControls } from "./chart-controls";
import { MetricCard } from "./metric-card";
import {
  generateChartData,
  generateChartDataset,
  getChartOptions,
} from "./utils";

interface Props {
  rows: GetLandingPageReportChart_me_landingPageReportChart_rows[];
}

export const ChartContent = ({ rows }: Props) => {
  const { user } = useUser();
  const prettyLogEventPage = usePrettyLogEventPage();

  const {
    filters: {
      attributionModel,
      attributionWindow,
      accountingMode,
      dateRange,
      dateRangeInterval,
      level,
      breakdownFilters,
      adQuery,
      urlQuery,
      advancedSearch,
    },
    chartState: { alignAxesScale, activeMetrics },
    metrics,
    setActiveMetrics,
  } = useLandingPageAnalyticsContext();

  const pageContext = {
    attributionModel,
    attributionWindow,
    accountingMode,
    level,
    dateRange,
    breakdownFilters,
    adQuery,
    urlQuery,
    advancedSearch,
  };

  const chartDataset = useMemo(() => {
    const chartData = generateChartData({
      metricsData: rows,
      metrics,
      activeMetrics,
      timeGranularity: "daily",
      dateRange: dateRangeInterval,
      timezone: user.timezone,
    });

    return generateChartDataset(chartData, dateRangeInterval, "daily");
  }, [rows, metrics, activeMetrics, dateRangeInterval, user.timezone]);

  return (
    <div>
      <div className="flex items-start">
        <div className="flex items-center flex-wrap gap-4 mb-4">
          {metrics.map((e, i) => (
            <MetricCard
              key={e.id}
              index={i}
              metric={e}
              active={activeMetrics.includes(e.id)}
              rows={rows}
              isSelectable={activeMetrics.length < 3}
              isActiveDisabled={activeMetrics.length === 1}
              onChange={() => {
                prettyLogEventPage({
                  domain: "Landing Page Analytics",
                  action: "onClick",
                  additionalActionInfo: "Change active metrics",
                  data: {
                    pageContext,
                  },
                });

                if (activeMetrics.includes(e.id)) {
                  if (activeMetrics.length > 1) {
                    setActiveMetrics(activeMetrics.filter((m) => m !== e.id));
                  }
                } else if (activeMetrics.length < 3) {
                  setActiveMetrics([...activeMetrics, e.id]);
                }
              }}
            />
          ))}
        </div>
        <div className="flex items-end gap-3 ml-12">
          <ChartControls rows={rows} />
        </div>
      </div>
      <div className="w-full h-[400px]">
        <Line
          datasetIdKey="id"
          plugins={[TitlesPlugin]}
          options={getChartOptions({
            timeGranularity: "daily",
            alignAxes: alignAxesScale,
            data: chartDataset,
            code: user.currencies.currentCurrency,
          })}
          data={chartDataset}
        />
      </div>
    </div>
  );
};
